import { useRef } from 'react'

import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Card,
  CardBody,
  Collapse,
  Flex,
  Heading,
  Icon,
  IconButton,
  Spacer,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { PencilIcon } from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import CaseworkDetailTable from './CaseworkDetailTable'
import HighlightedText from './HighlightedText'
import PriorCaseworkAlert from './PriorCaseworkAlert'
import { convertDistrictToNumeric, hasPermission } from '../../../utils'
import {
  AddressText,
  CaseworkCMList,
  CaseworkPriorityIndicator,
  ConditionalRender,
  FormattedDate,
  NoValue,
  Person,
  Section,
} from '../../ui'
import { formatCaseNum } from '../../utils'

const CaseworkCard = ({ casework, hideConstituent }) => {
  const navigate = useNavigate()
  const user = useSelector(state => state.auth.user)
  const { isOpen, onOpen } = useDisclosure()

  const menuButtonRef = useRef(null)
  const menuRef = useRef(null)

  const handleExpandCasework = e => {
    if (
      menuButtonRef.current?.contains(e.target) ||
      menuRef.current?.contains(e.target)
    )
      return
    onOpen()
  }

  const {
    case_num,
    priority,
    status,
    last_updated,
    address,
    assigned_staff,
    referral_staff,
    intake_staff,
    opened_at,
    closed_at,
    created_at,
    district,
    contact_methods,
    intake_staff_headline,
    referral_staff_headline,
    assigned_staff_headline,
    address_headline,
    is_active,
    is_prior,
    actions: { editable },
  } = casework

  const url = `/casework/${status === 'Archived' ? 'archive/' : ''}${case_num}`

  const canViewDetails =
    district === user.district ||
    hasPermission(user, 'casework.view_all_casework')

  return (
    <Card>
      <CardBody
        cursor={isOpen ? 'default' : 'pointer'}
        onClick={handleExpandCasework}
      >
        <Stack>
          <Flex>
            <Stack direction='row' align='center'>
              <CaseworkPriorityIndicator priority={priority} height={10} />
              {/* Casework Number */}
              <Heading
                size='lg'
                color='nyccBlue.800'
                _dark={{ color: 'blue.200' }}
              >
                #{formatCaseNum(casework)}
              </Heading>
            </Stack>

            <Spacer />
            <Box align='end'>
              <Button variant='outline' onClick={() => navigate(url)}>
                View
              </Button>

              <ConditionalRender condition={editable}>
                <Tooltip label='Edit casework' placement='bottom-end'>
                  <IconButton
                    icon={<Icon as={PencilIcon} fontSize='2xl' />}
                    variant='ghost'
                    onClick={() => navigate(`/casework/${case_num}/edit`)}
                    ml={1}
                  />
                </Tooltip>
              </ConditionalRender>

              <Tooltip label='Open page in new tab' placement='bottom-end'>
                <IconButton
                  icon={<ExternalLinkIcon fontSize='2xl' />}
                  variant='ghost'
                  onClick={() => window.open(url, '_blank')}
                  ml={1}
                />
              </Tooltip>
            </Box>
          </Flex>

          {is_prior && is_active && <PriorCaseworkAlert casework={casework} />}

          <ConditionalRender condition={canViewDetails}>
            <Flex
              gap={{ base: 4, lg: 10 }}
              direction={{ base: 'column', lg: 'row' }}
            >
              <CaseworkDetailTable
                {...casework}
                tableVariant='unstyled'
                filtered
                hideConstituent={hideConstituent}
              />

              <Stack gap={3} w='xs'>
                {/* Assigned Staff */}
                <Section
                  heading='Assigned Staff'
                  direction={{ base: 'row', lg: 'column' }}
                  align={{ base: 'center', lg: 'start' }}
                >
                  {assigned_staff?.length > 0 ? (
                    (() => {
                      if (assigned_staff_headline) {
                        const highlightedSegments =
                          assigned_staff_headline.split(', ')

                        return assigned_staff.map((staff, index) => {
                          const matchedSegment = highlightedSegments.find(
                            segment => {
                              const cleanSegment = segment
                                .replace(/<\/?mark>/g, '')
                                .replace(/\s+/g, ' ')
                                .trim()
                              const staffName = staff.full_name
                                .replace(/\s+/g, ' ')
                                .trim()
                              return cleanSegment === staffName
                            }
                          )

                          return (
                            <Person
                              key={index}
                              deactivated={!staff?.is_active}
                              link={`/directory/staff/${staff.id}`}
                              name={staff.full_name}
                              highlightedName={matchedSegment || null}
                            />
                          )
                        })
                      }

                      return assigned_staff.map((staff, index) => (
                        <Person
                          key={index}
                          deactivated={!staff?.is_active}
                          link={`/directory/staff/${staff.id}`}
                          name={staff.full_name}
                        />
                      ))
                    })()
                  ) : (
                    <NoValue />
                  )}
                </Section>

                {intake_staff && (
                  <Section
                    heading='Intake By'
                    direction={{ base: 'row', lg: 'column' }}
                    align={{ base: 'center', lg: 'start' }}
                  >
                    <Person
                      link={`/directory/staff/${intake_staff.id}`}
                      name={intake_staff.full_name}
                      highlightedName={intake_staff_headline}
                    />
                  </Section>
                )}

                {referral_staff && (
                  <Section heading='Referral By'>
                    <Person
                      link={`/directory/staff/${referral_staff.id}`}
                      name={referral_staff.full_name}
                      highlightedName={referral_staff_headline}
                    />
                  </Section>
                )}

                <Section heading='Council Member'>
                  <CaseworkCMList casework={casework} />
                </Section>
              </Stack>
            </Flex>
          </ConditionalRender>
        </Stack>

        <Collapse in={isOpen} animateOpacity>
          {canViewDetails ? (
            <Card
              p={5}
              mt={2}
              bg='gray.100'
              _dark={{ bg: 'gray.500' }}
              borderColor='transparent'
              borderRadius='2xl'
            >
              <Stack direction='row'>
                <Section heading='Reporting Address'>
                  <Box>
                    {address_headline ? (
                      <HighlightedText
                        html={address_headline}
                        maxLength={200}
                      />
                    ) : (
                      <AddressText address={address} />
                    )}
                  </Box>
                </Section>
                {/* Last Updated */}
                <Section heading='Last Updated'>
                  <FormattedDate date={last_updated} direction='column' />
                </Section>

                <Section heading='Contact Method'>
                  <Text>
                    {contact_methods?.length ? (
                      contact_methods.map(cm => cm.name).join(', ')
                    ) : (
                      <NoValue />
                    )}
                  </Text>
                </Section>

                {status === 'REQUESTED' && (
                  <Section heading='Submitted'>
                    <FormattedDate date={created_at} direction='column' />
                  </Section>
                )}

                <Section heading='Opened'>
                  <FormattedDate date={opened_at} direction='column' />
                </Section>

                <Section heading='Closed'>
                  <FormattedDate date={closed_at} direction='column' />
                </Section>
              </Stack>
            </Card>
          ) : (
            <Text>
              This casework is {status}. Please contact{' '}
              <b> District {convertDistrictToNumeric(district)} </b>
              for information about this casework.
            </Text>
          )}
        </Collapse>
      </CardBody>
    </Card>
  )
}

export default CaseworkCard
